import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { X } from "lucide-react";
import { toast } from "sonner";

const REASONS_IN = ["PURCHASE", "RETURN", "ADJUSTMENT"];
const REASONS_OUT = ["SALE", "DAMAGED", "ADJUSTMENT"];

export default function StockMovementModal({ product, type, onClose, onSaved }) {
  const [size, setSize] = useState(product.sizes?.[0]?.size || "");
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState(type === "IN" ? "PURCHASE" : "SALE");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await api.post("/stock-movements", {
        product_id: product.id,
        size, type,
        quantity: Number(quantity) || 0,
        reason, notes,
      });
      toast.success(`Stock ${type} recorded`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="stock-movement-modal">
      <div className="brut-card w-full max-w-md bg-white">
        <div className={`flex items-center justify-between p-5 border-b-2 border-black ${type === "IN" ? "bg-[#00C853]" : "bg-[#FF3333] text-white"}`}>
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-70">RECORD</div>
            <h2 className="font-display font-black text-2xl tracking-tight">Stock {type}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/10" data-testid="close-stock-modal"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="font-mono-ds text-[11px] tracking-widest uppercase">{product.name} · {product.sku}</div>

          <Field label="Size">
            <select className="brut-input" value={size} onChange={(e) => setSize(e.target.value)} data-testid="movement-size-select">
              {product.sizes?.map((s) => <option key={s.size} value={s.size}>{s.size} — current {s.stock}</option>)}
            </select>
          </Field>

          <Field label="Quantity">
            <input type="number" min={1} className="brut-input font-mono-ds" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="movement-quantity-input" />
          </Field>

          <Field label="Reason">
            <select className="brut-input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="movement-reason-select">
              {(type === "IN" ? REASONS_IN : REASONS_OUT).map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>

          <Field label="Notes">
            <textarea rows={2} className="brut-input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="movement-notes-input" />
          </Field>
        </div>

        <div className="border-t-2 border-black p-5 flex justify-end gap-3">
          <button onClick={onClose} className="brut-btn-outline px-5 py-2.5">Cancel</button>
          <button onClick={submit} disabled={busy} className="brut-btn px-5 py-2.5" data-testid="confirm-movement-btn">
            {busy ? "Saving…" : `Record ${type}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">{label}</div>
      {children}
    </label>
  );
}
